import {
  type AbilityTuple,
  createMongoAbility,
  type MongoAbility,
} from '@casl/ability';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import gravatar from 'gravatar';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type JSX,
} from 'react';
import { useNavigate } from 'react-router';

import { NotificationContext } from '@client/context/NotificationContext';
import { defineAbilityFor } from '@client/hooks/useAbilities';
import {
  defineRole,
  getIdToken,
  getUserClaims,
} from '@client/services/auth.service';
import { firebaseAuth } from '@client/services/firebase.config';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths, TNotificationConfig } from '@client/types';
import { logger } from '@client/utils/logger.util';

import type { TLogin, TRegister } from '@client/pages/auth/auth.page';
import type { TUserRole } from '@common/types';

export interface AuthContextType {
  user: User | null;
  ability: MongoAbility;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (data: TLogin) => Promise<void>;
  registerWithEmail: (data: TRegister) => Promise<void>;
  signOut: () => Promise<void>;
  accessToken?: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

/**
 * Provides authentication-related state and functions to its children.
 * @param {{children}} root0 - The parameters object.
 * @param {React.ReactNode} root0.children - The children to be rendered within the AuthProvider.
 * @returns {JSX.Element} The AuthProvider component.
 */
export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { setLoading } = useLoading();
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string>(null);
  const [ability, setAbility] = useState<MongoAbility>(
    createMongoAbility<AbilityTuple>([]) as MongoAbility,
  );

  const { notificationApi } = useContext(NotificationContext);

  const navigate = useNavigate();

  const updateAbility = useCallback(
    (user: User | null, role: TUserRole | null) => {
      setAbility(defineAbilityFor({ user, role }));
    },
    [setAbility],
  );

  const clearUser = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    updateAbility(null, null);
  }, [setUser, updateAbility]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      async (user: User | null) => {
        if (user) {
          setUser(user);

          const claims = await getUserClaims();
          let token = await getIdToken(user);

          if (claims?.role) {
            logger.info('Role is already available in claims', claims?.role);
            updateAbility(user, claims?.role as TUserRole);
            setAccessToken(token);
          } else {
            await defineRole(user);
            token = await getIdToken(user, true);
            const claims = await getUserClaims(user);

            if (claims?.role) {
              updateAbility(user, claims?.role as TUserRole);
              setAccessToken(token);
            } else {
              logger.warn('No role found for user, clearing session');
              clearUser();
            }
          }
        } else {
          clearUser();
        }
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [clearUser, updateAbility, setLoading]);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebaseAuth, provider);
      navigate(ERoutePaths.HOME);
    } catch (error: any) {
      notificationApi?.error({
        title: `Error: ${error.errorType ?? error.name}`,
        description: error.message,
        ...TNotificationConfig,
      });
      logger.error('Error during Google sign in:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithEmail = async (data: TLogin) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(firebaseAuth, data.email, data.password);
      navigate(ERoutePaths.HOME);
    } catch (error: any) {
      logger.error('Error signing in with email:', error);
      notificationApi?.error({
        title: `Error: ${error.errorType ?? error.name}`,
        description: error.message,
        ...TNotificationConfig,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmail = async (data: TRegister) => {
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        data.email,
        data.password,
      );
      const photoURL = gravatar.url(data?.email);
      const displayName = `${data.firstName} ${data.lastName}`;

      await updateProfile(userCredential.user, {
        displayName,
        photoURL,
      });

      // Refetch user to get updated profile
      setUser({
        ...userCredential.user,
        displayName,
        photoURL,
      });

      navigate(ERoutePaths.HOME);
    } catch (error: any) {
      notificationApi?.error({
        title: `Error: ${error.errorType ?? error.name}`,
        description: error.message,
        ...TNotificationConfig,
      });
      logger.error('Error registering user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(firebaseAuth);
      clearUser();
      navigate(ERoutePaths.LOGIN);
    } catch (error) {
      logger.error('Error during sign out:', error);
    }
  };

  const value = {
    user,
    accessToken,
    ability,
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
